import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './BackgroundEffects.css';

// Leaf configuration
const leafImages = ['p1', 'p2', 'p3', 'p4', 'p5'];
const leafSizes = { p1: 28, p2: 16, p3: 24, p4: 12, p5: 18 };

// Generate random leaf properties
const createLeaf = (id) => {
  const img = leafImages[Math.floor(Math.random() * leafImages.length)];
  return {
    id,
    img,
    size: leafSizes[img],
    left: Math.random() * 90 + 5, // 5-95%
    fallDuration: 6 + Math.random() * 3, // 6-9s fall time
  };
};

export default function BackgroundEffects() {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [leaves, setLeaves] = useState([]);
  const leafIdRef = useRef(0);

  // Spawn leaves at random intervals
  useEffect(() => {
    if (!isHomePage) {
      setLeaves([]);
      return;
    }

    const spawnLeaf = () => {
      const id = leafIdRef.current++;
      const newLeaf = createLeaf(id);
      setLeaves(prev => [...prev, newLeaf]);

      // Remove leaf after animation completes
      const totalTime = (newLeaf.fallDuration + 12) * 1000;
      setTimeout(() => {
        setLeaves(prev => prev.filter(l => l.id !== id));
      }, totalTime);
    };

    // Initial leaves (staggered)
    const initialTimeouts = [];
    for (let i = 0; i < 3; i++) {
      initialTimeouts.push(setTimeout(spawnLeaf, i * 1000));
    }

    // Continue spawning slowly
    const spawnInterval = setInterval(() => {
      spawnLeaf();
    }, 3000); // One leaf every 3 seconds

    return () => {
      initialTimeouts.forEach(clearTimeout);
      clearInterval(spawnInterval);
    };
  }, [isHomePage]);

  return (
    <>
      {/* Background layer - shapes (behind white section) */}
      <div className="bg-effects">
        <div className="bg-geometric-pattern">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern
                id="bg-pattern"
                width="260"
                height="260"
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x="145"
                  y="30"
                  width="30"
                  height="30"
                  fill="none"
                  stroke="black"
                  strokeWidth="5"
                  strokeOpacity="0.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  transform="rotate(68 162 47)"
                />
                <rect
                  x="50"
                  y="158"
                  width="30"
                  height="30"
                  fill="none"
                  stroke="black"
                  strokeWidth="5"
                  strokeOpacity="0.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  transform="rotate(123 65 175)"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#bg-pattern)" />
          </svg>
        </div>
      </div>

      {/* Foreground layer - falling leaves */}
      {isHomePage && (
        <div className="bg-leaf-field visible">
          {leaves.map((leaf) => (
            <div
              key={leaf.id}
              className="bg-leaf-fall"
              style={{
                left: `${leaf.left}%`,
                animationDuration: `${leaf.fallDuration + 12}s`, // fall + rest
              }}
            >
              <div
                className="bg-leaf"
                style={{
                  backgroundImage: `url(${process.env.PUBLIC_URL}/particles/${leaf.img}.png)`,
                  width: `${leaf.size}px`,
                  height: `${leaf.size}px`,
                  animationDuration: `${leaf.fallDuration + 12}s`,
                  '--sway-duration': `${leaf.swayDuration}s`,
                }}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
